import type { PayloadAction } from '@reduxjs/toolkit';
import { BeneficiaryCreationResponse } from '@/domains/beneficiaries/types/api/responses/BeneficiaryCreationResponse';
import { HttpServiceResponse } from '@/services/HttpService/types';
import { RootState } from '@/store/types/RootState';
import { CreateBeneficiaryAutomaticallyRequest } from '../types/api/requests/CreateBeneficiaryAutomaticallyRequest';
import { CreateBeneficiaryManuallyRequest } from '../types/api/requests/CreateBeneficiaryManuallyRequest';
import { call, delay, put, select, takeLatest } from 'redux-saga/effects';
import { BeneficiaryDrawerParams } from '@/features/drawer/components/BeneficiaryDrawer/enums/BeneficiaryDrawerParams';
import { Drawers } from '@/features/drawer/enums/Drawers';
import { DrawerService } from '@/features/drawer/services/DrawerService/DrawerService';
import { Modals } from '@/features/serviceModal/enums/Modals';
import { SnackType } from '@/features/snacks/enums/SnackType';
import { getBeneficiaryList } from '@/domains/beneficiaries/store/beneficiarySlice';
import HttpService from '@/services/HttpService/HttpService';
import ModalService from '@/services/ModalService/ModalService';
import NavigationService from '@/services/NavigationService/NavigationService';
import SnacksService from '@/services/SnackService/SnackService';
import { checkContactInformation } from '@/utils/checkContactInformation';
import { defineCreateBeneficiarySuccessMessage } from '../constants/defineCreateBeneficiarySuccessMessage';
import {
  createBeneficiaryAutomatically,
  createBeneficiaryManually,
  createBeneficiarySuccess,
  resetCreateBeneficiaryState,
  setCreateBeneficiaryLoadingStatus,
} from '../store/createBeneficiarySlice';

function* createBeneficiaryManuallySaga({
  payload: { body, relationshipType, searchParam },
}: PayloadAction<CreateBeneficiaryManuallyRequest>) {
  try {
    yield put(setCreateBeneficiaryLoadingStatus(true));

    const requestSearchParam = searchParam ? `?${searchParam}=true` : '';
    const { data }: HttpServiceResponse<BeneficiaryCreationResponse> = yield call(
      HttpService.post,
      `/examiner/beneficiaries/create-${relationshipType}-beneficiary-manually${requestSearchParam}`,
      { ...body, contactInformation: checkContactInformation(body.contactInformation) },
    );

    yield put(getBeneficiaryList({ claimId: String(body.claimId) }));
    yield put(createBeneficiarySuccess());

    SnacksService.add({
      type: SnackType.Success,
      title: defineCreateBeneficiarySuccessMessage(
        body.business?.businessName ?? `${body.person?.firstName} ${body.person?.lastName}`,
      ),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });
    ModalService.close(Modals.AddPotentialBeneficiary);

    yield delay(300);

    NavigationService.navigateTo(
      [
        location.pathname,
        DrawerService.prepareSearchParams(Drawers.Beneficiary, {
          [BeneficiaryDrawerParams.ClaimId]: body.claimId,
          [BeneficiaryDrawerParams.BeneficiaryId]: data.id,
          [BeneficiaryDrawerParams.Expanded]: true,
        }),
      ].join('?'),
      { replace: true },
    );
  } catch (error) {
    console.error(error);
    ModalService.close(Modals.AddPotentialBeneficiary);
  } finally {
    yield put(setCreateBeneficiaryLoadingStatus(false));
    yield put(resetCreateBeneficiaryState());
  }
}

function* createBeneficiaryAutomaticallySaga({ payload }: PayloadAction<CreateBeneficiaryAutomaticallyRequest>) {
  try {
    yield put(setCreateBeneficiaryLoadingStatus(true));

    const { data }: HttpServiceResponse<BeneficiaryCreationResponse> = yield call(
      HttpService.post,
      `/examiner/beneficiaries/create-beneficiary-automatically`,
      payload,
    );

    yield put(getBeneficiaryList({ claimId: String(payload.claimId) }));
    yield put(createBeneficiarySuccess());

    const userForBeneficiaryCreate = yield select(
      (state: RootState) => state.createBeneficiary.userForBeneficiaryCreate,
    );
    SnacksService.add({
      type: SnackType.Success,
      title: defineCreateBeneficiarySuccessMessage(userForBeneficiaryCreate?.name ?? ''),
      anchorOrigin: {
        vertical: 'bottom',
        horizontal: 'right',
      },
    });

    yield delay(300);

    NavigationService.navigateTo(
      [
        location.pathname,
        DrawerService.prepareSearchParams(Drawers.Beneficiary, {
          [BeneficiaryDrawerParams.ClaimId]: payload.claimId,
          [BeneficiaryDrawerParams.BeneficiaryId]: data.id,
          [BeneficiaryDrawerParams.Expanded]: true,
        }),
      ].join('?'),
      { replace: true },
    );
  } catch (error) {
    console.error(error);
  } finally {
    yield put(setCreateBeneficiaryLoadingStatus(false));
    yield put(resetCreateBeneficiaryState());
    ModalService.close(Modals.AddPotentialBeneficiary);
  }
}

export function* createBeneficiaryWatcher() {
  yield takeLatest(createBeneficiaryManually.type, createBeneficiaryManuallySaga);
  yield takeLatest(createBeneficiaryAutomatically.type, createBeneficiaryAutomaticallySaga);
}
